import React, { FocusEvent, ChangeEvent, useState } from 'react';
import styled from 'styled-components';
import { Typography, OutlinedInput, Button as MUIButton, Divider } from '@mui/material';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import RequiredLabel from '@ui-elements/RequiredLabel';
import MessageArea from '@ui-parts/MessageArea';
import LinkButton from '@ui-parts/LinkButton';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { PayloadAction } from '@reduxjs/toolkit';
import { BUTTON, FONT_SIZES, PALETTE } from '~/assets/styles/base';
import { GMOASK_STATUS } from '~/constants/api';
import { useAppDispatch } from '~/App/hooks';
import InputValidationErrorBox from '~/components/ui-parts/InputValidationErrorBox';
import { validateUtf8mb4 } from '~/constants/validationRules';
import { ROUTES } from '~/constants/routes';
import { UserInquiryResponse } from '~/common/types/api/users';
import { createUserInquiry } from '~/apiTs';
import { USER_INQUIRY } from '~/common/constants';

const InquiryBox = styled.div``;

const RegisterBox = styled.div`
  width: 720px;
  margin: 0 auto;
  background-color: ${PALETTE.white};
  border: 1px solid ${PALETTE.border.frame};
`;

const TitleBox = styled.div`
  width: 100%;
  padding: 46px 56px;
  white-space: pre-line;
  font-family: 'Noto Sans JP';
`;

const Title = styled(Typography)`
  color: ${PALETTE.text.primary};
  font-size: ${FONT_SIZES.title1};
  font-weight: bold;
  text-align: center;
  width: 100%;
  margin-bottom: 15px;
`;

type DescProps = {
  $textAlign?: string;
  $paddingLeft?: string;
};
const Desc = styled(Typography)<DescProps>`
  color: ${PALETTE.text.main};
  font-size: ${FONT_SIZES.title2};
  text-align: ${({ $textAlign }) => $textAlign || 'left'};
  padding-left: ${({ $paddingLeft }) => $paddingLeft || '100px'};
`;

const InputArea = styled.div`
  & div[data-testid='uiParts-InputValidationErrorBox'] {
    & > div {
      z-index: 1;
      margin-top: 5px;
      margin-left: 166px;
    }
  }
  & textarea {
    max-height: 300px;
    overflow-y: auto !important;
    background: ${PALETTE.blue};
  }
`;

const InputWrapper = styled.div`
  width: 100%;
  padding: 0 110px;
  margin-bottom: 20px;
  & .MuiDivider-root {
    margin: 20px 0;
  }
`;

const RequiredLabelWrapper = styled.div`
  display: flex;
  margin-right: 31px;
`;

const Label = styled(Typography)`
  color: ${PALETTE.text.main};
  font-size: ${FONT_SIZES.title3};
  text-align: left;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-flow: wrap;
  justify-content: center;
  gap: 11px;
  margin-bottom: 46px;
`;

const ConfirmArea = styled.div`
  margin-bottom: 30px;
`;
const ContentArea = styled(Typography)`
  color: ${PALETTE.text.main};
  font-size: ${FONT_SIZES.body2};
  text-align: left;
  white-space: pre-line;
  font-family: 'Noto Sans JP';
  margin-top: 10px;
`;

const CompletedBox = styled.div`
  max-width: 706px;
  border: 1px solid ${PALETTE.border.frame};
  margin: 0 auto;
`;

const StyledTaskAltIcon = styled(TaskAltIcon)`
  color: ${PALETTE.text.primary};
  font-size: 4rem;
`;

const UserInquiry: React.FC<Record<string, never>> = (): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const [inquirySubject, setInquirySubject] = useState('');
  const [inquiryMessage, setInquiryMessage] = useState('');
  const [isSubjectValid, setIsSubjectValid] = useState(true);
  const [isMessageValid, setIsMessageValid] = useState(true);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [lockSend, setLockSend] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  const handleOnChangeSubjectField = (
    e: React.ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>
  ) => {
    setInquirySubject(e.target.value);
    setLockSend(false);
    setIsSubjectValid(true);
  };

  const handleOnChangeMessageField = (
    e: React.ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>
  ) => {
    setInquiryMessage(e.target.value);
    setLockSend(false);
    setIsMessageValid(true);
  };

  const getErrorMessage = (msg: string) => {
    if (!validateUtf8mb4(msg)) {
      return t('userInquiry.validation.errorUtf8');
    }
    return t('userInquiry.validation.errorRequired');
  };

  const onSubmitInquiry = async () => {
    const lastAccessUri = `${searchParams.get(USER_INQUIRY.REQUEST_PARAM)}`.substring(0, 100);
    const params = {
      subject: inquirySubject.trim(),
      message: inquiryMessage.trim(),
      lastAccessUri,
    };
    const { payload: response } = (await dispatch(
      createUserInquiry(params)
    )) as unknown as PayloadAction<
      UserInquiryResponse,
      string,
      { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
      never
    >;
    if (response.status === GMOASK_STATUS.OK) {
      setIsCompleted(true);
    }
  };

  const confirmInquiry = () => {
    const isValidSubject = inquirySubject.trim() !== '' && validateUtf8mb4(inquirySubject);
    const isValidMessage = inquiryMessage.trim() !== '' && validateUtf8mb4(inquiryMessage);
    setIsSubjectValid(isValidSubject);
    setIsMessageValid(isValidMessage);
    const isValid = isValidSubject && isValidMessage;
    setIsConfirmed(isValid);
    setLockSend(!isValid);
    if (isConfirmed) {
      onSubmitInquiry();
    }
  };

  const backToModify = () => {
    setIsConfirmed(false);
    setLockSend(false);
  };

  return (
    <InquiryBox>
      {!isCompleted && (
        <RegisterBox data-testid="uiPages-inquiry-register-box">
          <TitleBox>
            <Title>{t('userInquiry.title')}</Title>
            {!isConfirmed && (
              <>
                <Desc>{t('userInquiry.description1')}</Desc>
                <Desc>{t('userInquiry.description2')}</Desc>
              </>
            )}
            {isConfirmed && (
              <Desc $textAlign="center" $paddingLeft="0px">
                {t('userInquiry.description3')}
              </Desc>
            )}
          </TitleBox>
          {!isConfirmed && (
            <InputArea>
              <InputWrapper>
                <RequiredLabelWrapper>
                  <RequiredLabel label={t('appCommon.requiredLabel')} color={PALETTE.error.main} />
                  <Label>{t('userInquiry.subject')}</Label>
                </RequiredLabelWrapper>
                <OutlinedInput
                  sx={{
                    marginTop: '5px',
                    marginBottom: '10px',
                    color: PALETTE.text.main,
                    width: '500px',
                    height: '40px',
                    fontSize: 14,
                    padding: '8px 15px',
                    borderRadius: `${BUTTON.squareRoundBorder}`,
                  }}
                  error={!isSubjectValid}
                  placeholder=""
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    handleOnChangeSubjectField(e);
                  }}
                  onBlur={(e: FocusEvent<HTMLInputElement>) => {
                    handleOnChangeSubjectField(e);
                  }}
                  value={inquirySubject}
                  inputProps={{
                    'data-testid': `UserInquirySubjectInput`,
                    maxLength: USER_INQUIRY.SUBJECT_MAXLENGTH,
                  }}
                />
                {!isSubjectValid && (
                  <InputValidationErrorBox
                    message={getErrorMessage(inquirySubject)}
                    position="centerAbsolute"
                    width="max-content"
                  />
                )}
              </InputWrapper>
              <InputWrapper>
                <RequiredLabelWrapper>
                  <RequiredLabel label={t('appCommon.requiredLabel')} color={PALETTE.error.main} />
                  <Label>{t('userInquiry.message')}</Label>
                </RequiredLabelWrapper>
                <OutlinedInput
                  sx={{
                    marginTop: '5px',
                    marginBottom: '10px',
                    color: PALETTE.text.main,
                    width: '500px',
                    fontSize: 14,
                    padding: '8px 15px',
                    borderRadius: `${BUTTON.squareRoundBorder}`,
                  }}
                  minRows={8}
                  multiline
                  error={!isMessageValid}
                  placeholder=""
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    handleOnChangeMessageField(e);
                  }}
                  onBlur={(e: FocusEvent<HTMLInputElement>) => {
                    handleOnChangeMessageField(e);
                  }}
                  value={inquiryMessage}
                  inputProps={{
                    'data-testid': `UserInquiryMessageInput`,
                    maxLength: USER_INQUIRY.MESSAGE_MAXLENGTH,
                  }}
                />
                {!isMessageValid && (
                  <InputValidationErrorBox
                    message={getErrorMessage(inquiryMessage)}
                    position="centerAbsolute"
                    width="max-content"
                  />
                )}
              </InputWrapper>
            </InputArea>
          )}
          {isConfirmed && (
            <ConfirmArea>
              <InputWrapper>
                <RequiredLabelWrapper>
                  <Label>{t('userInquiry.subject')}</Label>
                </RequiredLabelWrapper>
                <ContentArea data-testid="UserInquirySubjectValue">{inquirySubject}</ContentArea>
                <Divider />
              </InputWrapper>
              <InputWrapper>
                <RequiredLabelWrapper>
                  <Label>{t('userInquiry.message')}</Label>
                </RequiredLabelWrapper>
                <ContentArea data-testid="UserInquiryMessageValue">{inquiryMessage}</ContentArea>
              </InputWrapper>
            </ConfirmArea>
          )}
          <ButtonWrapper>
            {isConfirmed && (
              <MUIButton
                onClick={backToModify}
                data-testid="UserInquiryBackButton"
                variant="outlined"
                sx={{
                  color: PALETTE.gray[300],
                  border: `1px solid ${PALETTE.gray[400]}`,
                  borderRadius: '4px',
                  backgroundColor: PALETTE.white,
                  fontSize: '16px',
                  fontWeight: 'normal',
                  width: '148px',
                }}
              >
                {t('userInquiry.back')}
              </MUIButton>
            )}
            <MUIButton
              onClick={confirmInquiry}
              disabled={lockSend}
              data-testid="UserInquiryConfirmButton"
              variant="contained"
              sx={{
                color: PALETTE.text.primary,
                fontSize: '16px',
                fontWeight: 'normal',
                width: '148px',
                borderRadius: '4px',
              }}
            >
              {t('userInquiry.confirm')}
            </MUIButton>
          </ButtonWrapper>
        </RegisterBox>
      )}

      {isCompleted && (
        <CompletedBox data-testid="uiPages-inquiry-completed-box">
          <MessageArea
            message={t('userInquiry.completed.message')}
            desc={t('userInquiry.completed.desc')}
            button={
              <LinkButton
                label={t('userInquiry.completed.linkButton')}
                variant="outlined"
                href={ROUTES.USER.MYPAGE}
              />
            }
          >
            <StyledTaskAltIcon />
          </MessageArea>
        </CompletedBox>
      )}
    </InquiryBox>
  );
};

export default UserInquiry;
